import { useState, useEffect, lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import LP1790Header from "@/components/layout/LP1790Header";
import HeroSection from "@/components/landing/HeroSection";
import TransformationAreas from "@/components/landing/TransformationAreas";
import useScrollToHash from "@/hooks/useScrollToHash";
import PerformanceOptimizer from "@/components/PerformanceOptimizer";
import heroBg from "@/assets/hero-background.jpg";

// Lazy load only lower sections
const TestimonialsSection = lazy(() => import("@/components/landing/TestimonialsSection"));
const TechnologySection = lazy(() => import("@/components/landing/TechnologySection"));
const ProcessSection = lazy(() => import("@/components/landing/ProcessSection"));
const PersonalUniverseSection1790 = lazy(() => import("@/components/landing/PersonalUniverseSection1790"));
const FAQSection = lazy(() => import("@/components/landing/FAQSection"));
const LandingFooter = lazy(() => import("@/components/landing/LandingFooter"));

const SectionSkeleton = () => (
  <div className="min-h-[400px] flex items-center justify-center">
    <div className="w-12 h-12 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
  </div>
);

const LP1790 = () => {
  const [scrollProgress, setScrollProgress] = useState(0);
  
  // Handle hash fragments in URL for navigation from blog
  useScrollToHash();
  
  // Safety fallback to ensure scroll is never locked on landing page
  useEffect(() => {
    document.body.style.overflow = "auto";
  }, []);
  
  // Debounced scroll handler for performance
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
          const progress = window.scrollY / totalHeight * 100;
          setScrollProgress(Math.min(progress, 100));
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // SEO metadata
  const title = "Numerologia Cabalística | Mapa Numerológico Completo – AuraCode";
  const description = "Descubra missão de vida e prosperidade com numerologia cabalística. Mapa numerológico completo com análise do nome, personalidade e propósito.";
  const url = "https://auracode.io/lp/mapa-numerologico-1790";
  const ogImage = "https://auracode.io/og-image.jpg";

  // Structured Data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [{
      "@type": "Organization",
      "@id": "https://auracode.io/#organization",
      "name": "AuraCode Tecnologia Ltda",
      "url": "https://auracode.io",
      "logo": {
        "@type": "ImageObject",
        "url": "https://auracode.io/logo.png",
        "width": 600,
        "height": 200
      },
      "description": "Especialistas em numerologia cabalística com tecnologia avançada para mapas numerológicos completos e descoberta de missão de vida.",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "BR",
        "addressRegion": "SP",
        "addressLocality": "São Paulo",
        "streetAddress": "Avenida Paulista, 777, Sala 102",
        "postalCode": "01310-100"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "areaServed": "BR",
        "availableLanguage": "Portuguese"
      }
    }, {
      "@type": "WebSite",
      "@id": "https://auracode.io/#website",
      "url": "https://auracode.io",
      "name": "AuraCode"
    }, {
      "@type": "Product",
      "@id": "https://auracode.io/lp/mapa-numerologico-1790#product",
      "name": "Mapa Numerológico Cabalístico AuraCode",
      "description": "Mapa numerológico completo baseado na numerologia cabalística para descobrir missão de vida, prosperidade e autoconhecimento através da análise do nome.",
      "category": "Numerologia",
      "brand": {
        "@type": "Brand",
        "name": "AuraCode"
      },
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "priceCurrency": "BRL"
      }
    }, {
      "@type": "FAQPage",
      "@id": "https://auracode.io/lp/mapa-numerologico-1790#faq",
      "mainEntity": [{
        "@type": "Question",
        "name": "O que é numerologia cabalística?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "A numerologia cabalística é um sistema antigo que analisa os números do seu nome e data de nascimento para revelar aspectos profundos da personalidade, missão de vida e caminhos para prosperidade."
        }
      }, {
        "@type": "Question",
        "name": "Como descobrir minha missão de vida?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Através do mapa numerológico completo, que analisa os números do seu nome completo e revela seu propósito de vida, talentos naturais e direcionamentos para realização pessoal e profissional."
        }
      }]
    }]
  };
  return (
    <div className="min-h-screen dark-theme">
      <Helmet prioritizeSeoTags>
        {/* Basic SEO */}
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1" />
        <meta name="googlebot" content="index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1" />
        <link rel="canonical" href={url} />

        {/* Performance - Preload LCP image */}
        <link rel="preload" as="image" href={heroBg} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="AuraCode – Numerologia Cabalística" />
        <meta property="og:locale" content="pt_BR" />
        <meta property="og:site_name" content="AuraCode" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImage} />
        <meta name="twitter:image:alt" content="AuraCode – Numerologia Cabalística" />
        <meta name="twitter:url" content={url} />

        {/* Hreflang */}
        <link rel="alternate" hrefLang="pt-BR" href={url} />
        <link rel="alternate" hrefLang="x-default" href={url} />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <PerformanceOptimizer />
      <LP1790Header />
      
      {/* Scroll Progress Indicator - Hidden on mobile */}
      <div className="dark-scroll-indicator hidden lg:block">
        <div className="dark-scroll-progress" style={{
          height: `${scrollProgress}%`
        }} />
      </div>
      
      <HeroSection />
      <TransformationAreas />
      
      <Suspense fallback={<SectionSkeleton />}>
        <TestimonialsSection />
      </Suspense>
      
      <Suspense fallback={<SectionSkeleton />}>
        <ProcessSection />
      </Suspense>
      
      <Suspense fallback={<SectionSkeleton />}>
        <TechnologySection />
      </Suspense>
      
      <Suspense fallback={<SectionSkeleton />}>
        <PersonalUniverseSection1790 />
      </Suspense>
      
      <Suspense fallback={<SectionSkeleton />}>
        <FAQSection />
      </Suspense>
      
      <Suspense fallback={<SectionSkeleton />}>
        <LandingFooter />
      </Suspense>
    </div>
  );
};

export default LP1790;
